import { MutableRefObject, useEffect, useMemo } from "react";
import { Input, Textarea } from "@chakra-ui/react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as distributionCenterFields from "utils/schemas/distributionCenter";
import { FocusableElement } from "components/Shared/DrawerForm";
import Form, { FormProps } from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import { DistributionCenterInput } from "__generated__/gql/graphql";
import AddressFieldset, {
  AddressFieldsetType,
} from "components/Shared/Forms/AddressFieldset";
import CreatableOrderingPlatformSelect from "components/Shared/Select/CreatableOrderingPlatformSelect";
import PhoneInput, {
  PhoneNumberFieldsetType,
} from "components/Shared/Forms/PhoneInput";
import MarkdownHelpText from "components/Shared/MarkdownHelpText";

export const FORM_FIELDS = [
  "orderingPlatformId",
  "name",
  "code",
  "notes",
] as const;

export type FormFields = Pick<
  DistributionCenterInput,
  (typeof FORM_FIELDS)[number]
> & {
  address: AddressFieldsetType;
  phoneNumber: PhoneNumberFieldsetType;
};

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  orderingPlatformId: distributionCenterFields.orderingPlatformId,
  name: distributionCenterFields.name,
  code: distributionCenterFields.code,
  notes: distributionCenterFields.notes,
  address: distributionCenterFields.address,
  phoneNumber: distributionCenterFields.phoneNumber,
});

const { FormField, ControlledFormField } =
  createFormFieldComponents<FormFields>({
    schema,
  });

export const processApolloError = makePostgraphileErrorProcessor<FormFields>(
  getPostgraphileErrorMatchersForFields<keyof FormFields>(FORM_FIELDS, {
    type: "DistributionCenter",
  }),
);

export type Props = Omit<FormProps<FormFields, object>, "formContext"> & {
  // eslint-disable-next-line react/boolean-prop-naming
  autoFocus?: boolean;
  defaultValues?: Partial<FormFields>;
  formId?: string;
  initialFocusRef?: MutableRefObject<FocusableElement | null>;
  isAdmin?: boolean;
  orderingPlatformId?: UUID;
};

export default function DistributionCenterForm({
  autoFocus,
  defaultValues: defaultValuesProp,
  children,
  formId,
  initialFocusRef,
  isAdmin,
  orderingPlatformId,
  ...rest
}: Props) {
  const defaultValues = useMemo(() => {
    if (!orderingPlatformId) return defaultValuesProp;
    return { ...defaultValuesProp, orderingPlatformId };
  }, [defaultValuesProp, orderingPlatformId]);

  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      formContext.reset(defaultValues);
    }
  }, [defaultValues, formContext]);

  return (
    <Form {...{ formContext }} id={formId} {...rest}>
      <ControlledFormField
        name="orderingPlatformId"
        isDisabled={!!orderingPlatformId}
        renderInput={({ field }) => (
          <CreatableOrderingPlatformSelect
            {...field}
            autoFocus={
              defaultValues?.orderingPlatformId ? undefined : autoFocus
            }
            isAdmin={isAdmin}
            isDisabled={!!orderingPlatformId}
          />
        )}
      />

      <FormField
        name="name"
        mt={0}
        autoFocus={defaultValues?.orderingPlatformId ? autoFocus : undefined}
        inputRef={initialFocusRef}
      />

      <FormField
        name="code"
        renderInput={(props) => <Input {...props} fontFamily="mono" />}
      />

      <FormField
        name="notes"
        helpText={<MarkdownHelpText />}
        InputComponent={Textarea}
      />

      <AddressFieldset<FormFields>
        fieldNamePrefix="address"
        legendLabel="Address"
        my={8}
      />

      <ControlledFormField
        name="phoneNumber.phone"
        renderInput={({ field }) => <PhoneInput {...field} />}
      />

      {children}
    </Form>
  );
}
